import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ConsultationBooking = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    role: '',
    phone: '',
    serviceInterest: '',
    urgency: '',
    currentChallenges: '',
    budget: '',
    preferredTime: '',
    agreeToTerms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const roleOptions = [
    { value: 'ceo', label: 'CEO/President', description: 'Chief Executive Officer' },
    { value: 'cto', label: 'CTO/VP Engineering', description: 'Chief Technology Officer' },
    { value: 'cfo', label: 'CFO/Finance Director', description: 'Chief Financial Officer' },
    { value: 'coo', label: 'COO/Operations', description: 'Chief Operating Officer' },
    { value: 'director', label: 'Director/Manager', description: 'Department Director or Manager' },
    { value: 'other', label: 'Other', description: 'Other role' }
  ];

  const serviceOptions = [
    { value: 'strategic', label: 'Strategic Assessment', description: 'AI strategy and planning' },
    { value: 'technical', label: 'Technical Implementation', description: 'Hands-on development' },
    { value: 'optimization', label: 'Ongoing Optimization', description: 'Performance improvement' },
    { value: 'unsure', label: 'Not Sure Yet', description: 'Need guidance on best fit' }
  ];

  const urgencyOptions = [
    { value: 'immediate', label: 'Immediate (Within 30 days)', description: 'Urgent business need' },
    { value: 'quarter', label: 'This Quarter (1-3 months)', description: 'Planned initiative' },
    { value: 'year', label: 'This Year (3-12 months)', description: 'Strategic planning' },
    { value: 'exploring', label: 'Just Exploring', description: 'Early research phase' }
  ];

  const budgetOptions = [
    { value: 'under100k', label: 'Under $100K', description: 'Small to medium projects' },
    { value: '100k-500k', label: '$100K - $500K', description: 'Medium scale implementations' },
    { value: '500k-1m', label: '$500K - $1M', description: 'Large scale transformations' },
    { value: 'over1m', label: 'Over $1M', description: 'Enterprise-wide initiatives' },
    { value: 'tbd', label: 'To Be Determined', description: 'Budget not yet defined' }
  ];

  const timeOptions = [
    { value: 'morning', label: 'Morning (9 AM - 12 PM EST)', description: 'Best for West Coast' },
    { value: 'afternoon', label: 'Afternoon (12 PM - 5 PM EST)', description: 'Standard business hours' },
    { value: 'evening', label: 'Evening (5 PM - 7 PM EST)', description: 'After hours availability' },
    { value: 'flexible', label: 'Flexible', description: 'Any time works' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 2000);
  };

  const isFormValid = formData?.firstName && formData?.lastName && formData?.email && 
                     formData?.company && formData?.role && formData?.serviceInterest && 
                     formData?.agreeToTerms;

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} color="white" />
        </div>
        
        <h3 className="text-2xl font-bold text-text-primary mb-4">
          Consultation Scheduled!
        </h3>
        
        <p className="text-text-secondary mb-6 max-w-md mx-auto">
          Thank you for your interest. Our team will review your information and contact you within 24 hours to schedule your consultation.
        </p>

        <div className="bg-brand-surface rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-text-primary mb-2">What happens next?</h4>
          <ul className="text-sm text-text-secondary space-y-1">
            <li>• Our team reviews your requirements</li>
            <li>• We'll email you within 24 hours</li>
            <li>• Schedule a 45-minute consultation call</li>
            <li>• Receive a custom AI strategy outline</li>
          </ul>
        </div>

        <Button
          variant="outline"
          onClick={() => {
            setIsSubmitted(false);
            setFormData({
              firstName: '', lastName: '', email: '', company: '', role: '',
              phone: '', serviceInterest: '', urgency: '', currentChallenges: '',
              budget: '', preferredTime: '', agreeToTerms: false
            });
          }}
          iconName="RotateCcw"
          iconPosition="left"
        >
          Schedule Another Consultation
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-brand-primary to-brand-secondary p-6">
        <div className="flex items-center space-x-3 mb-2">
          <Icon name="Calendar" size={24} color="white" />
          <h3 className="text-xl font-bold text-white">
            Schedule Your Consultation
          </h3>
        </div>
        <p className="text-white/90">
          Get a custom AI strategy session with our experts. No sales pitch, just actionable insights.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="First Name"
            type="text"
            placeholder="John"
            value={formData?.firstName}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            required
          />

          <Input
            label="Last Name"
            type="text"
            placeholder="Smith"
            value={formData?.lastName}
            onChange={(e) => handleInputChange('lastName', e?.target?.value)}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Business Email"
            type="email"
            placeholder="john@company.com"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            required
          />

          <Input
            label="Phone Number"
            type="tel"
            placeholder="+1 (619) 800-1235"
            value={formData?.phone}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
            description="Optional - for urgent follow-ups"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Company Name"
            type="text"
            placeholder="Acme Corporation"
            value={formData?.company}
            onChange={(e) => handleInputChange('company', e?.target?.value)}
            required
          />

          <Select
            label="Your Role"
            options={roleOptions}
            value={formData?.role}
            onChange={(value) => handleInputChange('role', value)}
            placeholder="Select your role..."
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Service Interest"
            options={serviceOptions}
            value={formData?.serviceInterest}
            onChange={(value) => handleInputChange('serviceInterest', value)}
            placeholder="What service interests you?"
            required
          />

          <Select
            label="Timeline"
            options={urgencyOptions}
            value={formData?.urgency}
            onChange={(value) => handleInputChange('urgency', value)}
            placeholder="When do you need this?"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Budget Range"
            options={budgetOptions}
            value={formData?.budget}
            onChange={(value) => handleInputChange('budget', value)}
            placeholder="Select budget range..."
            description="Helps us prepare relevant solutions"
          />

          <Select
            label="Preferred Call Time"
            options={timeOptions}
            value={formData?.preferredTime}
            onChange={(value) => handleInputChange('preferredTime', value)}
            placeholder="Best time for you..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Current AI Challenges
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none"
            rows={4}
            placeholder="Briefly describe your current AI challenges or goals..."
            value={formData?.currentChallenges}
            onChange={(e) => handleInputChange('currentChallenges', e?.target?.value)}
          />
          <p className="text-xs text-text-secondary mt-1">
            Optional - helps us prepare for our conversation
          </p>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <Checkbox
            label="I agree to the consultation terms and privacy policy"
            description="We'll use this information to prepare for your consultation and may follow up with relevant resources."
            checked={formData?.agreeToTerms}
            onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
            required
          />
        </div>

        <Button
          type="submit"
          variant="default"
          disabled={!isFormValid}
          loading={isSubmitting}
          fullWidth
          className="bg-brand-primary hover:bg-brand-primary/90"
          iconName="Calendar"
          iconPosition="right"
        >
          {isSubmitting ? 'Scheduling...' : 'Schedule Free Consultation'}
        </Button>

        <div className="text-center">
          <p className="text-sm text-text-secondary">
            Free 45-minute consultation • No sales pressure • Actionable insights guaranteed
          </p>
        </div>
      </form>
    </div>
  );
};

export default ConsultationBooking;